/**
 * Street address normalization — frontend mirror of
 * backend/utils/address_normalizer.py.
 *
 * Used by CreateOrderForm to render a live preview ("Will be saved as: …")
 * so users see exactly what the backend will store. Backend remains the
 * single source of truth — frontend is purely cosmetic.
 *
 * Keep these rules in sync with address_normalizer.py.
 */

const SUFFIXES = {
  st: 'St', str: 'Str', street: 'Street',
  ave: 'Ave', avenue: 'Avenue',
  blvd: 'Blvd', boulevard: 'Boulevard',
  rd: 'Rd', road: 'Road',
  dr: 'Dr', drive: 'Drive',
  ln: 'Ln', lane: 'Lane',
  ct: 'Ct', court: 'Court',
  cir: 'Cir', circle: 'Circle',
  pkwy: 'Pkwy', parkway: 'Parkway',
  hwy: 'Hwy', highway: 'Highway',
  pl: 'Pl', place: 'Place',
  ter: 'Ter', terrace: 'Terrace',
  way: 'Way',
  loop: 'Loop',
  trl: 'Trl', trail: 'Trail',
  sq: 'Sq', square: 'Square',
  pt: 'Pt', point: 'Point',
  cv: 'Cv', cove: 'Cove',
  bnd: 'Bnd', bend: 'Bend',
  pass: 'Pass',
  xing: 'Xing', crossing: 'Crossing',
  row: 'Row',
};

const DIRECTIONALS = new Set(['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw']);

const UNIT_MARKERS = {
  apt: 'Apt', apartment: 'Apartment',
  unit: 'Unit',
  ste: 'Ste', suite: 'Suite',
  fl: 'Fl', floor: 'Floor',
  bldg: 'Bldg', building: 'Building',
  rm: 'Rm', room: 'Room',
  lot: 'Lot',
  no: 'No',
  '#': '#',
};

const NUMERIC_LETTER_RE = /^\d+[A-Za-z]+$|^[A-Za-z]+\d+$|^\d+[\-/]\d+$|^\d+-?[A-Za-z]+$/;
const FRACTION_RE = /^\d+\/\d+$/;
const ORDINAL_RE = /^(\d+)(st|nd|rd|th)$/i;

function formatToken(token) {
  if (!token) return token;
  const trailingMatch = token.match(/[.,;:]+$/);
  const trailing = trailingMatch ? trailingMatch[0] : '';
  const bare = trailing ? token.slice(0, -trailing.length) : token;
  if (!bare) return token;
  const lower = bare.toLowerCase();

  if (/^\d+$/.test(bare)) return bare + trailing;

  // Ordinals first (5th not 5TH).
  const ordMatch = bare.match(ORDINAL_RE);
  if (ordMatch) return ordMatch[1] + ordMatch[2].toLowerCase() + trailing;

  if (FRACTION_RE.test(bare)) return bare + trailing;
  if (NUMERIC_LETTER_RE.test(bare)) {
    return [...bare].map((c) => (/[a-zA-Z]/.test(c) ? c.toUpperCase() : c)).join('') + trailing;
  }

  if (DIRECTIONALS.has(lower)) return lower.toUpperCase() + trailing;
  if (SUFFIXES[lower]) return SUFFIXES[lower] + trailing;
  if (UNIT_MARKERS[lower]) return UNIT_MARKERS[lower] + trailing;

  if (bare.indexOf('-') !== -1) {
    return bare.split('-').map(formatToken).join('-') + trailing;
  }

  return bare.charAt(0).toUpperCase() + bare.slice(1).toLowerCase() + trailing;
}

export function normalizeStreetAddress(raw) {
  if (raw === null || raw === undefined) return raw;
  const text = String(raw).trim();
  if (!text) return '';
  const collapsed = text.replace(/\s+/g, ' ');
  return collapsed.split(' ').map(formatToken).join(' ');
}

export default normalizeStreetAddress;
